import type {
  CollectionReference,
  PartialWithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { addDocWithError, getDocsWithError } from '../../firestore-mobx/fetch'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SectionAssignmentConversationItem } from '../../models/SectionAssignmentConversationItem'
import type { FirestoreSectionAssignmentConversationItem } from './schema'
import { schema, writeSchema } from './schema'
import type { WriteBatch } from 'firebase/firestore'

const converter: FirestoreDataConverter<FirestoreSectionAssignmentConversationItem> =
  {
    toFirestore: (
      data: PartialWithFieldValue<FirestoreSectionAssignmentConversationItem>
    ) => {
      writeSchema.partial().parse(data)
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const empty = Object.freeze(schema.parse({}))

const getColRef = (
  firestore: Firestore,
  {
    sectionId,
    assignmentId,
    conversationId,
  }: { sectionId: string; assignmentId: string; conversationId: string }
): CollectionReference<FirestoreSectionAssignmentConversationItem> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'conversation',
    conversationId,
    'conversation_item'
  ).withConverter(converter)
}

export const getSectionAssignmentConversationItemsStream = (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string; conversationId: string }
): StreamInterface<SectionAssignmentConversationItem[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  return collectionSnapshots(conversationsQuery).map((snapshot) => {
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        SectionAssignmentConversationItem
      )
    })
    return models
  })
}

export const fetchSectionAssignmentConversationItems = async (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string; conversationId: string }
): Promise<SectionAssignmentConversationItem[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  const snapshot = await getDocsWithError(
    conversationsQuery,
    'FetchConversationItemsError'
  )
  const models = snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(
      repository,
      doc,
      SectionAssignmentConversationItem
    )
  })
  return models
}

export const createSectionAssignmentConversationItem = async (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    conversationId: string
    userId: string
    role: 'user' // only user is supported from the client side
    userContent: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    conversationId: params.conversationId,
  })

  const data = {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    conversationId: params.conversationId,
    userId: params.userId,
    role: params.role,
    userContent: params.userContent,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const doc = await addDocWithError(ref, data, 'CreateConversationItemError')
  return doc.id
}

export const batchDeleteSectionAssignmentConversationItem = (
  repository: FirebaseRepository,
  params: {
    batch: WriteBatch
    sectionId: string
    assignmentId: string
    conversationId: string
    conversationItemId: string
  }
) => {
  const ref = getColRef(repository.firestore, {
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    conversationId: params.conversationId,
  })

  const docRef = doc(ref, params.conversationItemId)

  return params.batch.delete(docRef)
}
